import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Card,
  CardContent,
  CardActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
} from '@material-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from '../../store';
import { 
  addPurchaseOrder, 
  updatePurchaseOrder, 
  fetchDropdownsStart,
  clearError 
} from '../../store/reducers/purchaseOrderSlice';
import { purchaseOrderApi, PurchaseOrder } from '../../services/purchaseOrderApi';
import { downloadDocument } from '../../utils/documentDownload';
import SerialImport from '../Common/SerialImport';

const PurchaseOrderEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { selectedPurchaseOrder, clients, uoms, poStatuses, loadingDropdowns, error } = useSelector((state: RootState) => state.purchaseOrder);
  
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder>({
    Number: '',
    PoDate: new Date().toISOString().split('T')[0],
    DeliveryDate: new Date().toISOString().split('T')[0],
    OrderQty: 0,
    UoMId: '',
    PricePerUnit: 0,
    ClientId: '',
    PoStatusId: 1,
    DeliveryAddressStreet: '',
    DeliveryZIP: '',
    DeliveryCity: '',
    State: '',
    Country: '',
    Serials: [],
    Documents: [],
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [documentLoading, setDocumentLoading] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    // Fetch dropdown data
    dispatch(fetchDropdownsStart());
  }, [dispatch]);

  useEffect(() => {
    if (id && id !== 'new') {
      setEditMode(true);
      loadPurchaseOrderData(id);
    } else {
      setEditMode(false);
      setPurchaseOrder({
        Number: '',
        PoDate: new Date().toISOString().split('T')[0],
        DeliveryDate: new Date().toISOString().split('T')[0],
        OrderQty: 0,
        UoMId: '',
        PricePerUnit: 0,
        ClientId: '',
        PoStatusId: 1,
        DeliveryAddressStreet: '',
        DeliveryZIP: '',
        DeliveryCity: '',
        State: '',
        Country: '',
        Serials: [],
        Documents: [],
      });
    }
  }, [id]);

  const loadPurchaseOrderData = async (poId: string) => {
    setLoading(true);
    try {
      const poData = await purchaseOrderApi.getById(poId);
      setPurchaseOrder(poData);
    } catch (error) {
      console.error('Error loading purchase order:', error);
      setValidationErrors(['Failed to load purchase order data. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof PurchaseOrder, value: any) => {
    setPurchaseOrder(prev => ({ ...prev, [field]: value }));
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const validatePurchaseOrder = (poData: PurchaseOrder): string[] => {
    const errors: string[] = [];
    
    // Required fields validation (matching backend schema)
    if (!poData.Number?.trim()) {
      errors.push('Purchase Order Number is required');
    } else if (poData.Number.length > 50) {
      errors.push('Purchase Order Number must be 50 characters or less');
    }
    
    if (!poData.PoDate?.trim()) {
      errors.push('PO Date is required');
    }
    
    if (!poData.DeliveryDate?.trim()) {
      errors.push('Delivery Date is required');
    }
    
    if (!poData.OrderQty || poData.OrderQty <= 0) {
      errors.push('Order Quantity must be greater than 0');
    }
    
    if (!poData.ClientId?.trim()) {
      errors.push('Client is required');
    } else if (isNaN(parseInt(poData.ClientId))) {
      errors.push('Client ID must be a valid number');
    }
    
    if (!poData.UoMId?.trim()) {
      errors.push('Unit of Measure is required');
    } else if (isNaN(parseInt(poData.UoMId))) {
      errors.push('UoM ID must be a valid number');
    }
    
    if (!poData.PoStatusId || poData.PoStatusId <= 0) {
      errors.push('PO Status is required');
    }
    
    // Address validation
    if (poData.DeliveryAddressStreet && poData.DeliveryAddressStreet.length > 200) {
      errors.push('Delivery Address Street must be 200 characters or less');
    }
    
    if (poData.State && poData.State.length > 200) {
      errors.push('State must be 200 characters or less');
    }
    
    if (poData.DeliveryCity && poData.DeliveryCity.length > 50) {
      errors.push('Delivery City must be 50 characters or less');
    }
    
    if (poData.DeliveryZIP && poData.DeliveryZIP.length > 20) {
      errors.push('Delivery ZIP must be 20 characters or less');
    }
    
    if (poData.Country && poData.Country.length > 20) {
      errors.push('Country must be 20 characters or less');
    }
    
    return errors;
  };

  const handleSave = async () => {
    setValidationErrors([]);
    
    // Validate the purchase order data
    const errors = validatePurchaseOrder(purchaseOrder);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setSaving(true);
    try {
      if (editMode) {
        // Update existing purchase order
        await purchaseOrderApi.update(purchaseOrder.id!, purchaseOrder);
        dispatch(updatePurchaseOrder(purchaseOrder));
      } else {
        // Create new purchase order
        const newPO = await purchaseOrderApi.create(purchaseOrder);
        dispatch(addPurchaseOrder(newPO));
      }
      
      navigate('/purchaseorders');
    } catch (error: any) {
      console.error('Error saving purchase order:', error);
      setValidationErrors([error.message || 'Failed to save purchase order. Please try again.']);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/purchaseorders');
  };

  const handleDeleteDocument = (documentId: string) => {
    setPurchaseOrder(prev => ({
      ...prev,
      Documents: prev.Documents?.filter(doc => doc.id !== documentId) || [],
    }));
  };

  const handleDownloadDocument = async (document: { id: string; Name: string }) => {
    setDocumentLoading(document.id);
    try {
      await downloadDocument(document.id);
    } catch (error) {
      console.error('Error downloading document:', error);
      // Error handling is done in the downloadDocument utility
    } finally {
      setDocumentLoading(null);
    }
  };

  const handleSerialsChange = (newSerials: { SerialNum: string }[]) => {
    // Convert to the format expected by the PurchaseOrder interface
    const formattedSerials = newSerials.map((serial, index) => ({
      id: serial.SerialNum, // Use SerialNum as ID for new serials
      SerialNum: serial.SerialNum,
      PartNumber: '', // Will be filled when part is selected
      PartDescription: '', // Will be filled when part is selected
      PartPOId: '', // Will be filled when saved
    }));

    setPurchaseOrder(prev => ({
      ...prev,
      Serials: formattedSerials,
    }));

    // Update order quantity to match serial count
    setPurchaseOrder(prev => ({
      ...prev,
      OrderQty: formattedSerials.length,
    }));
  };

  const handleErrorClose = () => {
    setValidationErrors([]);
    dispatch(clearError());
  };

  // Convert Serials to the format expected by SerialImport
  const serialsForImport = purchaseOrder.Serials?.map(serial => ({
    SerialNum: serial.SerialNum,
  })) || [];

  if (loadingDropdowns) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {editMode ? 'Edit Purchase Order' : 'Add New Purchase Order'}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleBack}
        >
          Back to Purchase Orders
        </Button>
      </Box>

      {/* Error handling */}
      {(error || validationErrors.length > 0) && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={handleErrorClose}
        >
          <Alert onClose={handleErrorClose} severity="error">
            {error || validationErrors.join(', ')}
          </Alert>
        </Snackbar>
      )}

      {/* Loading state */}
      {loading && (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      )}

      <Paper>
        <Box p={3}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Purchase Order Number"
                value={purchaseOrder.Number}
                onChange={(e) => handleInputChange('Number', e.target.value)}
                disabled={editMode}
                margin="normal"
                required
                inputProps={{ maxLength: 50 }}
                helperText={`${purchaseOrder.Number?.length || 0}/50 characters`}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Client</InputLabel>
                <Select
                  value={purchaseOrder.ClientId}
                  onChange={(e) => handleInputChange('ClientId', e.target.value)}
                >
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PO Date"
                type="date"
                value={purchaseOrder.PoDate}
                onChange={(e) => handleInputChange('PoDate', e.target.value)}
                margin="normal"
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Delivery Date"
                type="date"
                value={purchaseOrder.DeliveryDate}
                onChange={(e) => handleInputChange('DeliveryDate', e.target.value)}
                margin="normal"
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Order Quantity"
                type="number"
                value={purchaseOrder.OrderQty || ''}
                onChange={(e) => handleInputChange('OrderQty', parseInt(e.target.value) || 0)}
                margin="normal"
                required
                inputProps={{ min: 1, step: 1 }}
                helperText="Enter quantity (must be greater than 0)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Unit of Measure</InputLabel>
                <Select
                  value={purchaseOrder.UoMId}
                  onChange={(e) => handleInputChange('UoMId', e.target.value)}
                >
                  {uoms.map((uom) => (
                    <MenuItem key={uom.id} value={uom.id}>
                      {uom.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Price Per Unit"
                type="number"
                value={purchaseOrder.PricePerUnit}
                onChange={(e) => handleInputChange('PricePerUnit', parseFloat(e.target.value))}
                margin="normal"
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Status</InputLabel>
                <Select
                  value={purchaseOrder.PoStatusId}
                  onChange={(e) => handleInputChange('PoStatusId', parseInt(e.target.value as string))}
                >
                  {poStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Delivery Address */}
            <Grid item xs={12}>
              <Divider style={{ margin: '2rem 0' }} />
              <Typography variant="h6" gutterBottom>
                Delivery Address
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={purchaseOrder.DeliveryAddressStreet}
                onChange={(e) => handleInputChange('DeliveryAddressStreet', e.target.value)}
                margin="normal"
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                value={purchaseOrder.DeliveryCity}
                onChange={(e) => handleInputChange('DeliveryCity', e.target.value)}
                margin="normal"
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="State"
                value={purchaseOrder.State}
                onChange={(e) => handleInputChange('State', e.target.value)}
                margin="normal"
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={purchaseOrder.DeliveryZIP}
                onChange={(e) => handleInputChange('DeliveryZIP', e.target.value)}
                margin="normal"
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country"
                value={purchaseOrder.Country}
                onChange={(e) => handleInputChange('Country', e.target.value)}
                margin="normal"
                required
              />
            </Grid>
          </Grid>

          <Divider style={{ margin: '2rem 0' }} />

          {/* Serial Numbers Section */}
          <Typography variant="h6" gutterBottom>
            Serial Numbers
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: '1rem' }}>
            Add serial numbers for this purchase order. The order quantity will automatically match the number of serials.
          </Typography>
          <SerialImport
            value={serialsForImport}
            onChange={handleSerialsChange}
            placeholder="Enter serial number and press Enter or Space"
            disabled={loading}
          />

          <Divider style={{ margin: '2rem 0' }} />

          {/* Documents Section */}
          <Typography variant="h6" gutterBottom>
            Documents
          </Typography>
          <Card variant="outlined" style={{ marginBottom: '1rem' }}>
            <CardContent>
              <Typography variant="body2" color="textSecondary">
                Add new documents or view existing ones
              </Typography>
            </CardContent>
            <CardActions>
              <Button size="small" startIcon={<AddIcon />}>
                Add Document
              </Button>
            </CardActions>
          </Card>

          {purchaseOrder.Documents && purchaseOrder.Documents.length > 0 && (
            <List>
              {purchaseOrder.Documents.map((doc) => (
                <ListItem key={doc.id}>
                  <ListItemText
                    primary={doc.Name}
                    secondary={`${doc.DocumentNum} - Version ${doc.Version}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleDownloadDocument(doc)}
                      disabled={documentLoading === doc.id}
                      title="Download Document"
                    >
                      {documentLoading === doc.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DownloadIcon />
                      )}
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteDocument(doc.id)}
                      title="Delete Document"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Box p={3} display="flex" justifyContent="space-between">
          <Button
            variant="outlined"
            onClick={handleBack}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save Purchase Order'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default PurchaseOrderEdit; 